import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Upload, Sparkles, FileText, Loader2, CheckCircle,
  X, AlertCircle, Pencil, RotateCcw, Building2, Award, Tag,
  ChevronDown, ChevronUp, Eye
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { invokeFunction } from "../../lib/invokeFunction";
import { extractTextFromPdf, isPdfFile } from "../../lib/pdfExtractor";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain"
];

function getFileTypeLabel(type) {
  if (type === "application/pdf") return "PDF";
  if (type.includes("word")) return "Word";
  if (type === "text/plain") return "TXT";
  return "File";
}

export default function AiProfileAssistant({ profile, setProfile, onClose }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [selectedFields, setSelectedFields] = useState({
    name: true,
    title: true,
    bio: true,
    companies: true,
    achievements: true,
    specializations: true
  });
  const [expandedSections, setExpandedSections] = useState({
    name: true,
    title: true,
    bio: true,
    companies: true,
    achievements: true,
    specializations: true
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      setError("Поддерживаются форматы: PDF, Word (.doc, .docx), TXT");
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      setError("Максимальный размер файла: 5 МБ");
      return;
    }

    setError("");
    setFile(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      const fakeEvent = { target: { files: [dropped] } };
      handleFileSelect(fakeEvent);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError("");

    try {
      let fileContent;
      let isBase64 = false;

      if (isPdfFile(file)) {
        console.log("[AI CV] Extracting PDF text on client with pdfjs-dist...");
        const pdfText = await extractTextFromPdf(file);
        console.log(`[AI CV] PDF extracted: ${pdfText.length} chars`);
        if (pdfText.length < 50) {
          throw new Error("Не удалось извлечь текст из PDF. Попробуйте сохранить резюме в формате TXT и загрузить снова.");
        }
        fileContent = pdfText;
        isBase64 = false;
      } else if (file.type === "text/plain") {
        fileContent = await file.text();
      } else {
        fileContent = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64data = reader.result.split(",")[1];
            resolve(base64data);
          };
          reader.readAsDataURL(file);
        });
        isBase64 = true;
      }

      const { data, error: fnError } = await invokeFunction("deepseek-parse-cv", {
        body: { fileContent, fileType: file.type, fileName: file.name, isBase64 }
      });

      if (fnError) {
        throw new Error(fnError.message || "Ошибка анализа резюме");
      }

      if (data?.profile) {
        setAiResult(data.profile);
        setStep("review");
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error("AI не смог извлечь данные из файла");
      }
    } catch (err) {
      console.error("AI analysis error:", err);
      setError(err.message || "Ошибка при анализе резюме");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!aiResult) return;

    const updates = {};

    if (selectedFields.name && aiResult.name) {
      updates.name = aiResult.name;
    }
    if (selectedFields.title && aiResult.title) {
      updates.title = aiResult.title;
    }
    if (selectedFields.bio && aiResult.bio) {
      updates.bio = aiResult.bio;
    }
    if (selectedFields.companies && aiResult.companies?.length > 0) {
      const existing = Array.isArray(profile.companies) ? profile.companies : [];
      updates.companies = [...existing, ...aiResult.companies];
    }
    if (selectedFields.achievements && aiResult.achievements?.length > 0) {
      const existing = Array.isArray(profile.achievements) ? profile.achievements : [];
      updates.achievements = [...existing, ...aiResult.achievements];
    }
    if (selectedFields.specializations && aiResult.specializations?.length > 0) {
      const existing = Array.isArray(profile.specializations) ? profile.specializations : [];
      const merged = [...new Set([...existing, ...aiResult.specializations])];
      updates.specializations = merged;
    }

    setProfile({ ...profile, ...updates });
    onClose();
  };

  const handleReset = () => {
    setFile(null);
    setAiResult(null);
    setError("");
    setStep("upload");
    setSelectedFields({
      name: true,
      title: true,
      bio: true,
      companies: true,
      achievements: true,
      specializations: true
    });
    setExpandedSections({
      name: true,
      title: true,
      bio: true,
      companies: true,
      achievements: true,
      specializations: true
    });
  };

  const toggleField = (field) => {
    setSelectedFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const toggleExpand = (field) => {
    setExpandedSections((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const countSelectedFields = () => {
    let total = 0;
    let selected = 0;
    if (aiResult?.name) { total++; if (selectedFields.name) selected++; }
    if (aiResult?.title) { total++; if (selectedFields.title) selected++; }
    if (aiResult?.bio) { total++; if (selectedFields.bio) selected++; }
    if (aiResult?.companies?.length > 0) { total++; if (selectedFields.companies) selected++; }
    if (aiResult?.achievements?.length > 0) { total++; if (selectedFields.achievements) selected++; }
    if (aiResult?.specializations?.length > 0) { total++; if (selectedFields.specializations) selected++; }
    return { total, selected };
  };

  return (
    <motion.div
      id="ai-profile-assistant_root"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-6 rounded-2xl bg-gradient-to-br from-primary-950/60 to-accent-950/40 border border-primary-600/30"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-primary-600/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h3 className="text-xl font-medium text-white">
              AI-помощник заполнения профиля
            </h3>
            <p className="text-lg font-extralight text-neutral-400">
              Загрузите резюме, и AI-помощник заполнит профиль за вас
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-md text-neutral-500 hover:text-white hover:bg-neutral-800/50 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <AnimatePresence mode="wait">
        {step === "upload" && (
          <motion.div
            key="upload"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {error && (
              <div className="flex items-center gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/20 mb-4">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-lg font-extralight text-red-300">{error}</p>
              </div>
            )}

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                file
                  ? "border-primary-500/40 bg-primary-500/5"
                  : "border-neutral-700 hover:border-primary-500/30 hover:bg-neutral-800/30"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />

              {file ? (
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-md bg-primary-500/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-medium text-white">{file.name}</p>
                    <p className="text-lg font-extralight text-neutral-400">
                      {getFileTypeLabel(file.type)} / {(file.size / 1024).toFixed(0)} КБ
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="p-2 rounded-md hover:bg-neutral-700 text-neutral-500 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-neutral-500 mx-auto mb-3" />
                  <p className="text-lg font-extralight text-neutral-300 mb-1">
                    Перетащите файл сюда или нажмите для выбора
                  </p>
                  <p className="text-lg font-extralight text-neutral-500">
                    PDF, Word (.doc, .docx), TXT / до 5 МБ
                  </p>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors"
              >
                Отмена
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!file || loading}
                className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Анализ резюме...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Проанализировать
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}

        {step === "review" && aiResult && (
          <motion.div
            key="review"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            <div className="flex items-start gap-3 p-4 rounded-md bg-accent-500/10 border border-accent-500/20">
              <Eye className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-lg font-medium text-accent-300 mb-1">
                  Результат анализа резюме
                </p>
                <p className="text-lg font-extralight text-accent-300/80">
                  Ниже показаны данные, извлечённые AI из вашего резюме. Просмотрите каждое поле, снимите галочку с тех, которые не хотите применять, и нажмите "Принять и продолжить".
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between px-1">
              <p className="text-lg font-extralight text-neutral-400">
                Выбрано полей: {countSelectedFields().selected} из {countSelectedFields().total}
              </p>
              <button
                onClick={() => {
                  const allSelected = Object.values(selectedFields).every(Boolean);
                  const newState = {};
                  Object.keys(selectedFields).forEach((k) => { newState[k] = !allSelected; });
                  setSelectedFields(newState);
                }}
                className="text-lg font-extralight text-primary-400 hover:text-primary-300 transition-colors"
              >
                {Object.values(selectedFields).every(Boolean) ? "Снять все" : "Выбрать все"}
              </button>
            </div>

            <ReviewField
              label="Имя"
              icon={<Pencil className="w-4 h-4" />}
              value={aiResult.name}
              selected={selectedFields.name}
              onToggle={() => toggleField("name")}
              expanded={expandedSections.name}
              onExpand={() => toggleExpand("name")}
              current={profile.name}
            />

            <ReviewField
              label="Должность"
              icon={<Pencil className="w-4 h-4" />}
              value={aiResult.title}
              selected={selectedFields.title}
              onToggle={() => toggleField("title")}
              expanded={expandedSections.title}
              onExpand={() => toggleExpand("title")}
              current={profile.title}
            />

            <ReviewField
              label="О себе"
              icon={<FileText className="w-4 h-4" />}
              value={aiResult.bio}
              selected={selectedFields.bio}
              onToggle={() => toggleField("bio")}
              expanded={expandedSections.bio}
              onExpand={() => toggleExpand("bio")}
              current={profile.bio}
              multiline
            />

            {aiResult.companies?.length > 0 && (
              <ReviewList
                label="Места работы"
                icon={<Building2 className="w-4 h-4" />}
                items={aiResult.companies.map(
                  (c) => `${c.name} — ${c.position} (${c.years})`
                )}
                selected={selectedFields.companies}
                onToggle={() => toggleField("companies")}
                expanded={expandedSections.companies}
                onExpand={() => toggleExpand("companies")}
              />
            )}

            {aiResult.achievements?.length > 0 && (
              <ReviewList
                label="Достижения"
                icon={<Award className="w-4 h-4" />}
                items={aiResult.achievements}
                selected={selectedFields.achievements}
                onToggle={() => toggleField("achievements")}
                expanded={expandedSections.achievements}
                onExpand={() => toggleExpand("achievements")}
              />
            )}

            {aiResult.specializations?.length > 0 && (
              <ReviewList
                label="Специализации"
                icon={<Tag className="w-4 h-4" />}
                items={aiResult.specializations}
                selected={selectedFields.specializations}
                onToggle={() => toggleField("specializations")}
                expanded={expandedSections.specializations}
                onExpand={() => toggleExpand("specializations")}
              />
            )}

            <div className="flex justify-between gap-3 pt-4">
              <button
                onClick={handleReset}
                className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-neutral-700 text-neutral-300 hover:text-white hover:border-neutral-500 text-lg font-extralight transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Загрузить другой файл
              </button>
              <button
                onClick={handleApply}
                disabled={countSelectedFields().selected === 0}
                className="flex items-center gap-2 px-6 py-2.5 rounded-md bg-accent-600 hover:bg-accent-500 disabled:bg-neutral-700 disabled:cursor-not-allowed text-white text-lg font-medium transition-colors"
              >
                <CheckCircle className="w-5 h-5" />
                Принять и продолжить
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ReviewField({ label, icon, value, selected, onToggle, expanded, onExpand, current, multiline }) {
  if (!value) return null;

  const hasExisting = current && current.length > 0;
  const hasChanged = hasExisting && current !== value;

  return (
    <div
      className={`rounded-md border transition-colors ${
        selected
          ? "border-primary-500/30 bg-primary-500/5"
          : "border-neutral-700/50 bg-neutral-800/20 opacity-60"
      }`}
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={onExpand}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={`w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${
              selected
                ? "bg-primary-600 border-primary-600"
                : "border-neutral-600 hover:border-neutral-400"
            }`}
          >
            {selected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </button>

          <div className="flex items-center gap-2 text-neutral-300">
            {icon}
            <span className="text-lg font-extralight">{label}</span>
          </div>

          {hasChanged && (
            <span className="text-lg font-extralight text-amber-400/80 ml-2">
              (будет изменено)
            </span>
          )}
          {!hasExisting && (
            <span className="text-lg font-extralight text-green-400/80 ml-2">
              (новое)
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!expanded && (
            <span className="text-lg font-extralight text-neutral-500 max-w-xs truncate hidden sm:block">
              {value.length > 50 ? value.slice(0, 50) + "..." : value}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-500" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 space-y-2">
              {hasChanged && (
                <div className="p-3 rounded bg-neutral-800/60">
                  <p className="text-lg font-extralight text-neutral-500 mb-1">Текущее значение:</p>
                  <p className="text-lg font-extralight text-neutral-400 line-through">
                    {current.length > 200 ? current.slice(0, 200) + "..." : current}
                  </p>
                </div>
              )}

              <div className="p-3 rounded bg-primary-500/5 border border-primary-500/10">
                <p className="text-lg font-extralight text-primary-400 mb-1">Новое значение от AI:</p>
                {multiline ? (
                  <p className="text-lg font-extralight text-white leading-relaxed whitespace-pre-line">
                    {value}
                  </p>
                ) : (
                  <p className="text-lg font-medium text-white">{value}</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReviewList({ label, icon, items, selected, onToggle, expanded, onExpand }) {
  if (!items || items.length === 0) return null;

  return (
    <div
      className={`rounded-md border transition-colors ${
        selected
          ? "border-primary-500/30 bg-primary-500/5"
          : "border-neutral-700/50 bg-neutral-800/20 opacity-60"
      }`}
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={onExpand}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={`w-5 h-5 rounded flex items-center justify-center border transition-colors flex-shrink-0 ${
              selected
                ? "bg-primary-600 border-primary-600"
                : "border-neutral-600 hover:border-neutral-400"
            }`}
          >
            {selected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
          </button>

          <div className="flex items-center gap-2 text-neutral-300">
            {icon}
            <span className="text-lg font-extralight">{label}</span>
            <span className="text-lg font-extralight text-neutral-500">
              ({items.length})
            </span>
          </div>

          <span className="text-lg font-extralight text-green-400/80 ml-2">
            (новое)
          </span>
        </div>

        <div className="flex items-center gap-2">
          {!expanded && (
            <span className="text-lg font-extralight text-neutral-500 hidden sm:block">
              {items.length} {items.length === 1 ? "элемент" : items.length < 5 ? "элемента" : "элементов"}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-neutral-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-neutral-500" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0">
              <div className="p-3 rounded bg-primary-500/5 border border-primary-500/10">
                <p className="text-lg font-extralight text-primary-400 mb-2">Извлечено AI:</p>
                <ul className="space-y-1.5">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-primary-400 mt-1 flex-shrink-0">-</span>
                      <span className="text-lg font-extralight text-white">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}